import type { FilterExpression, QuerySpec } from '@/types/query';

/**
 * Deterministic hash of a QuerySpec for cache lookups.
 *
 * Uses sorted-key JSON serialization → FNV-1a 32-bit hash → hex string.
 * Filters are a set (AND-combined), so their insertion order must not
 * affect the hash — we canonicalize by sorting before stringifying.
 */
export function hashSpec(spec: QuerySpec): string {
  const canonical: QuerySpec = { ...spec, filters: sortFilters(spec.filters) };
  const json = stableStringify(canonical);
  return fnv1a32(json);
}

/** Sort filters into a canonical order so [A,B] and [B,A] hash identically. */
function sortFilters(filters: FilterExpression[]): FilterExpression[] {
  return [...filters].sort((a, b) => {
    const ka = `${a.column}|${a.operator}|${JSON.stringify(a.value)}|${a.source ?? 'manual'}`;
    const kb = `${b.column}|${b.operator}|${JSON.stringify(b.value)}|${b.source ?? 'manual'}`;
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}

/** JSON.stringify with sorted keys for determinism. */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return '[' + value.map(stableStringify).join(',') + ']';
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    const parts = keys.map(
      (k) => JSON.stringify(k) + ':' + stableStringify((value as Record<string, unknown>)[k]),
    );
    return '{' + parts.join(',') + '}';
  }
  return String(value);
}

/** FNV-1a 32-bit hash → 8-char hex string. */
function fnv1a32(str: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
